import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  LayoutDashboard, Lightbulb, PenTool, FileText, Bookmark,
  Video, Mic, Calendar, CreditCard, LogOut, Menu, X, ChevronDown, Crown, Clock, AlertTriangle, HelpCircle, Zap, BarChart3
} from 'lucide-react';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Account status checks
  const isTrialActive = user?.accountStatus === 'trial_active';
  const isTrialExpired = user?.accountStatus === 'trial_expired';
  const isCancelled = user?.accountStatus === 'cancelled';
  const isGracePeriod = user?.accountStatus === 'grace_period';
  const isSubscribed = user?.accountStatus === 'active';
  const isBlocked = isTrialExpired || isCancelled;

  // Show header upgrade: blocked, or < 20% credits remaining
  const PLAN_CREDIT_CAPS = { beginner: 50, pro: 200, max: 600 };
  const totalCredits = isTrialActive
    ? (user?.trialCredits || 0)
    : (user?.credits || 0) + (user?.purchasedCredits || 0);
  const maxCredits = isTrialActive ? 20 : (PLAN_CREDIT_CAPS[user?.subscriptionPlan] || totalCredits || 1);
  const showHeaderUpgrade = isBlocked || (maxCredits > 0 && totalCredits > 0 && (totalCredits / maxCredits) <= 0.2);

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/ideas', icon: Lightbulb, label: 'Idea Generator' },
    { path: '/writing', icon: PenTool, label: 'Writing Tools' },
    { path: '/scripts', icon: FileText, label: 'Script Builder' },
    { path: '/saved', icon: Bookmark, label: 'Saved Content' },
    { path: '/editor', icon: Video, label: 'Editor', pro: true },
    { path: '/voiceovers', icon: Mic, label: 'AI Voiceovers', pro: true },
    { path: '/analyzer', icon: BarChart3, label: 'Content Analyzer', pro: true },
    { path: '/planner', icon: Calendar, label: '30-Day Planner', max: true },
    { path: '/billing', icon: CreditCard, label: 'Billing' },
    { path: '/help', icon: HelpCircle, label: 'Help & Support' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getPlanDisplayName = (plan, accountStatus) => {
    if (accountStatus === 'trial_active') return 'TRIAL';
    if (accountStatus === 'trial_expired') return 'EXPIRED';
    if (accountStatus === 'cancelled') return 'CANCELLED';
    if (accountStatus === 'grace_period') return 'PAYMENT ISSUE';
    if (plan === 'max') return 'CREATOR MAX';
    if (plan === 'pro') return 'PRO CREATOR';
    if (plan === 'beginner') return 'BEGINNER';
    return 'NO PLAN';
  };

  const getPlanColor = (plan, accountStatus) => {
    if (accountStatus === 'trial_active') return 'text-amber-400';
    if (accountStatus === 'trial_expired') return 'text-red-400';
    if (accountStatus === 'cancelled') return 'text-amber-400';
    if (accountStatus === 'grace_period') return 'text-orange-400';
    if (plan === 'max') return 'text-cyan-400';
    if (plan === 'pro') return 'text-violet-400';
    if (plan === 'beginner') return 'text-emerald-400';
    return 'text-slate-400';
  };

  // Calculate trial days remaining using floor
  const getTrialDaysRemaining = () => {
    if (!user?.trialEndTimestamp) return 0;
    const endDate = new Date(user.trialEndTimestamp);
    const now = new Date();
    const diffMs = endDate - now;
    if (diffMs <= 0) return 0;
    const fullDaysRemaining = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hasPartialDay = (diffMs % (1000 * 60 * 60 * 24)) > 0;
    return fullDaysRemaining + (hasPartialDay ? 1 : 0);
  };

  const NavItem = ({ item }) => {
    // For trial/beginner users, Pro features are locked
    const userPlan = user?.subscriptionPlan;
    const isPro = userPlan === 'pro' || userPlan === 'max';
    const isMax = userPlan === 'max';
    
    const showProBadge = item.pro && !isPro && !isBlocked;
    const showMaxBadge = item.max && !isMax && !isBlocked;
    const isLocked = (item.pro && !isPro) || (item.max && !isMax) || isBlocked;
    
    return (
      <NavLink
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
            isActive
              ? 'bg-slate-800/80 text-white border border-slate-700/50'
              : isLocked
                ? 'text-slate-600 hover:text-slate-400 hover:bg-slate-800/20'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/30'
          }`
        }
        data-testid={`nav-${item.path.slice(1)}`}
      >
        <item.icon className={`w-5 h-5 ${isLocked ? 'opacity-50' : ''}`} />
        <span className={`font-medium flex-1 ${isLocked ? 'opacity-50' : ''}`}>{item.label}</span>
        {showProBadge && (
          <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 text-xs px-2">
            <Crown className="w-3 h-3 mr-1" />
            PRO
          </Badge>
        )}
        {showMaxBadge && (
          <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-xs px-2">
            <Crown className="w-3 h-3 mr-1" />
            MAX
          </Badge>
        )}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen bg-[#0f1629]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0f1629]/95 backdrop-blur-xl border-b border-slate-800 px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <img src="/cf-logo.png" alt="CF" className="w-8 h-8 rounded-lg" />
            <span className="font-bold text-xl bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">CreatorFlow</span>
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-slate-400"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 h-full w-56 bg-[#0f1629] border-r border-slate-800/50 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-4 py-6">
            <div className="flex items-center gap-2.5">
              <img src="/cf-logo.png" alt="CF" className="w-9 h-9 rounded-lg" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                CreatorFlow
              </h1>
            </div>
          </div>

          {/* Trial Banner in Sidebar */}
          {isTrialActive && (
            <div className="px-3 pb-3">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm font-medium">Trial Active</span>
                </div>
                <p className="text-amber-200/70 text-xs">{getTrialDaysRemaining()} days • {user?.trialCredits || 0} credits</p>
                <Button 
                  size="sm" 
                  className="w-full mt-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 text-xs"
                  onClick={() => navigate('/billing')}
                >
                  Upgrade Now
                </Button>
              </div>
            </div>
          )}

          {/* Trial Expired Banner */}
          {isTrialExpired && (
            <div className="px-3 pb-3">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 text-sm font-medium">Trial Ended</span>
                </div>
                <p className="text-red-200/70 text-xs">Upgrade to continue</p>
                <Button 
                  size="sm" 
                  className="w-full mt-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 text-xs"
                  onClick={() => navigate('/billing')}
                  data-testid="sidebar-upgrade-btn"
                >
                  Upgrade Now
                </Button>
              </div>
            </div>
          )}

          {/* Cancelled Banner */}
          {isCancelled && (
            <div className="px-3 pb-3">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm font-medium">Subscription Ended</span>
                </div>
                <p className="text-amber-200/70 text-xs">Renew to continue</p>
                <Button 
                  size="sm" 
                  className="w-full mt-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 text-xs"
                  onClick={() => navigate('/billing')}
                  data-testid="sidebar-renew-btn"
                >
                  Renew Plan
                </Button>
              </div>
            </div>
          )}

          {/* Grace Period Banner */}
          {isGracePeriod && (
            <div className="px-3 pb-3">
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 text-orange-400" />
                  <span className="text-orange-400 text-sm font-medium">Payment Issue</span>
                </div>
                <p className="text-orange-200/70 text-xs">Update payment method</p>
                <Button 
                  size="sm" 
                  className="w-full mt-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 border border-orange-500/30 text-xs"
                  onClick={() => navigate('/billing')}
                  data-testid="sidebar-update-payment-btn"
                >
                  Update Payment
                </Button>
              </div>
            </div>
          )}

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3">
            <nav className="space-y-1">
              {navItems.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </nav>
          </ScrollArea>

          {/* Plan Badge */}
          <div className="p-3">
            <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-bold ${getPlanColor(user?.subscriptionPlan, user?.accountStatus)}`}>
                    {getPlanDisplayName(user?.subscriptionPlan, user?.accountStatus)}
                  </p>
                  <p className="text-xs text-slate-500">
                    {isTrialActive ? 'Limited access' : isBlocked ? (isCancelled ? 'Renew to unlock' : 'Subscribe to unlock') : isGracePeriod ? 'Update payment' : 'Access enabled'}
                  </p>
                </div>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  isTrialActive ? 'bg-amber-500/20' :
                  isTrialExpired ? 'bg-red-500/20' :
                  isCancelled ? 'bg-amber-500/20' :
                  isGracePeriod ? 'bg-orange-500/20' :
                  user?.subscriptionPlan === 'max' ? 'bg-cyan-500/20' : 'bg-violet-500/20'
                }`}>
                  {isTrialActive ? (
                    <Clock className="w-4 h-4 text-amber-400" />
                  ) : isTrialExpired ? (
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                  ) : isCancelled ? (
                    <CreditCard className="w-4 h-4 text-amber-400" />
                  ) : isGracePeriod ? (
                    <AlertTriangle className="w-4 h-4 text-orange-400" />
                  ) : (
                    <CreditCard className={`w-4 h-4 ${
                      user?.subscriptionPlan === 'max' ? 'text-cyan-400' : 'text-violet-400'
                    }`} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* User Menu */}
          <div className="p-3 border-t border-slate-800/50">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start gap-3 text-slate-300 hover:text-white hover:bg-slate-800/50 px-2"
                >
                  <div className="flex-1 text-left overflow-hidden">
                    <p className="text-sm font-medium truncate">{user?.email}</p>
                    <p className="text-xs text-slate-500 truncate">{getPlanDisplayName(user?.subscriptionPlan, user?.accountStatus)}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-slate-900 border-slate-800">
                <DropdownMenuItem 
                  onClick={() => navigate('/billing')}
                  className="cursor-pointer text-slate-300"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Billing
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="cursor-pointer text-red-400 focus:text-red-400"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-56 min-h-screen pt-16 lg:pt-0">
        {/* Header Upgrade Bar */}
        {showHeaderUpgrade && (
          <div className="sticky top-0 z-20 bg-gradient-to-r from-violet-600/90 to-cyan-600/90 backdrop-blur-sm border-b border-violet-500/30 px-6 py-2.5 flex items-center justify-between" data-testid="header-upgrade-bar">
            <span className="text-white/90 text-sm font-medium">
              {isBlocked ? (isCancelled ? 'Your subscription has ended.' : 'Your trial has ended.') : 'Credits running low!'}
            </span>
            <Button
              size="sm"
              className="bg-white text-violet-700 hover:bg-white/90 font-semibold text-xs px-4"
              onClick={() => navigate('/billing')}
              data-testid="header-upgrade-btn"
            >
              <Zap className="w-3.5 h-3.5 mr-1.5" />
              {isBlocked ? 'Upgrade Now' : 'Get More Credits'}
            </Button>
          </div>
        )}
        <div className="p-6 lg:p-8 max-w-5xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* DEV Plan Switcher - REMOVED */}
    </div>
  );
};

export default Layout;
